/**
 * EsimMarketplaceModal — Full-screen modal listing eSIM packages with filters.
 * - Search bar (country)
 * - Country chips (top destinations)
 * - Package cards with country flag, operator, data, validity, price
 * - "Buy" → Stripe Checkout
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Search, Wifi, Calendar, Database, Globe, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const EsimMarketplaceModal = ({ onClose, presetCountryCode = null }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(presetCountryCode || null);
  const [search, setSearch] = useState('');
  const [packages, setPackages] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [buying, setBuying] = useState(null);

  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const res = await axios.get('/api/esim/countries');
      setCountries(res.data?.countries || []);
    } catch {
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const loadPackages = useCallback(async (cc) => {
    if (!cc) {
      setPackages([]);
      return;
    }
    setLoadingPackages(true);
    try {
      const res = await axios.get(`/api/esim/packages?country_code=${encodeURIComponent(cc)}`);
      setPackages(res.data?.packages || []);
    } catch {
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    if (selectedCountry) loadPackages(selectedCountry);
  }, [selectedCountry, loadPackages]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        (c.country_title || '').toLowerCase().includes(q) ||
        (c.country_code || '').toLowerCase().includes(q)
    );
  }, [countries, search]);

  const handleBuy = async (pkg) => {
    setBuying(pkg.package_id);
    try {
      const res = await axios.post('/api/esim/checkout', {
        package_id: pkg.package_id,
      });
      const url = res.data?.checkout_url;
      const orderId = res.data?.order_id;
      if (!url || !orderId) throw new Error('no_url');

      if (Capacitor.isNativePlatform()) {
        // Open Stripe in the in-app browser so the user comes back to the
        // app as soon as they finish or cancel.
        await Browser.open({ url, presentationStyle: 'popover' });
        const sub = await Browser.addListener('browserFinished', async () => {
          sub.remove();
          // Navigate to success page which polls until the order is active or failed
          onClose?.();
          navigate(`/esim/success?order_id=${orderId}`);
        });
      } else {
        // Web: classic redirect — Stripe success_url will bring the user back
        window.location.href = url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || t('esim.paymentFailed');
      toast.error(typeof msg === 'string' ? msg : t('esim.paymentFailed'));
      setBuying(null);
    }
  };

  const selectedCountryObj = countries.find((c) => c.country_code === selectedCountry);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md flex flex-col"
      data-testid="esim-marketplace-modal"
    >
      {/* Header */}
      <div className="flex-shrink-0 sticky top-0 z-10 bg-gradient-to-b from-black/90 to-black/60 backdrop-blur-md border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={selectedCountry ? () => setSelectedCountry(null) : onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            data-testid="esim-modal-back-btn"
          >
            {selectedCountry ? (
              <ChevronLeft className="w-5 h-5 text-white" />
            ) : (
              <X className="w-5 h-5 text-white" />
            )}
          </button>
          <div className="flex-1">
            <h2 className="text-white font-bold text-lg leading-tight" data-testid="esim-modal-title">
              {selectedCountry ? selectedCountryObj?.country_title || t('esim.browseTitle') : t('esim.browseTitle')}
            </h2>
            <p className="text-white/60 text-xs leading-tight">{t('esim.poweredBy')}</p>
          </div>
        </div>

        {/* Search bar (only when browsing countries) */}
        {!selectedCountry && (
          <div className="max-w-3xl mx-auto px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('esim.searchDestination')}
                className="w-full bg-white/10 border border-white/15 rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/50 focus:outline-none focus:border-purple-400/50"
                data-testid="esim-search-input"
              />
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-4 pb-24">
          {/* Country grid */}
          {!selectedCountry && (
            <>
              {loadingCountries ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className="aspect-square rounded-xl bg-white/5 animate-pulse" />
                  ))}
                </div>
              ) : filteredCountries.length === 0 ? (
                <div className="text-center text-white/50 text-sm py-12">
                  {t('esim.countriesEmpty')}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="esim-countries-grid">
                  {filteredCountries.map((c) => (
                    <button
                      key={c.country_code + (c.country_slug || '')}
                      type="button"
                      onClick={() => setSelectedCountry(c.country_code)}
                      className="text-left bg-white/5 hover:bg-white/10 active:scale-95 rounded-xl border border-white/10 overflow-hidden transition group"
                      data-testid={`esim-modal-country-${c.country_code}`}
                    >
                      <div className="aspect-[4/3] w-full bg-gradient-to-br from-indigo-500/20 to-pink-500/20 overflow-hidden">
                        {c.country_image ? (
                          <img
                            src={c.country_image}
                            alt={c.country_title}
                            className="w-full h-full object-cover group-hover:scale-105 transition"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Globe className="w-10 h-10 text-white/40" />
                          </div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <div className="text-sm font-semibold text-white truncate">{c.country_title}</div>
                        <div className="text-[11px] text-white/60 mt-0.5 flex justify-between">
                          <span>{c.package_count} plans</span>
                          <span className="font-bold text-purple-300">{t('esim.from')} ${c.min_price_usd?.toFixed(2)}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Package list */}
          {selectedCountry && (
            <>
              {selectedCountryObj && (
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  {selectedCountryObj.country_image && (
                    <img
                      src={selectedCountryObj.country_image}
                      alt={selectedCountryObj.country_title}
                      className="w-full h-32 object-cover"
                    />
                  )}
                  <div className="p-3">
                    <div className="text-white font-bold text-base">{selectedCountryObj.country_title}</div>
                    <div className="text-white/60 text-xs">{selectedCountryObj.package_count} plans</div>
                  </div>
                </div>
              )}

              {loadingPackages ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />
                  ))}
                </div>
              ) : packages.length === 0 ? (
                <div className="text-center text-white/50 text-sm py-12">{t('esim.empty')}</div>
              ) : (
                <div className="space-y-3" data-testid="esim-packages-list">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.package_id}
                      className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-4"
                      data-testid={`esim-pkg-${pkg.package_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {pkg.operator_image ? (
                              <img src={pkg.operator_image} alt="" className="w-6 h-6 rounded object-cover" />
                            ) : (
                              <Wifi className="w-5 h-5 text-purple-400" />
                            )}
                            <span className="text-xs text-white/60 truncate">{pkg.operator_title}</span>
                          </div>
                          <h3 className="text-white font-bold text-base leading-tight">{pkg.title}</h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-white/70">
                            <span className="flex items-center gap-1">
                              <Database className="w-3.5 h-3.5" />
                              {pkg.is_unlimited ? t('esim.unlimited') : pkg.data_label}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {pkg.validity_days} {pkg.validity_days > 1 ? t('esim.days') : t('esim.day')}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-bold text-lg">${pkg.retail_price_usd?.toFixed(2)}</div>
                          <div className="text-[10px] text-white/40">USD</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={buying === pkg.package_id}
                        onClick={() => handleBuy(pkg)}
                        className="mt-3 w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm py-2.5 rounded-xl transition shadow-lg shadow-purple-500/20"
                        data-testid={`esim-buy-${pkg.package_id}`}
                      >
                        {buying === pkg.package_id ? t('esim.paying') : t('esim.buyNow')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EsimMarketplaceModal;
